/**
 * 视频档位的价格与「自动升档」说明(1004 T4 / T5)。
 *
 * ★价格一律从价目表取(`/pricing/catalog`,服务端逐行真调 priceOf,与 `/pricing/quote` 同值),
 *   前端不写一个数字。查不到 → null,UI 不显示数(不编)。
 */
import { SKU, formatPerSecondRate, type PriceBook } from "@/entities/pricing/model/priceBook";
import { videoProviderLabel } from "@/shared/lib/video/labels";

import type {
  GenerationMode,
  VideoCapabilitiesResponse,
  VideoProviderCapability,
  VideoResolution,
} from "../api/videoService";
import { autoPickModel } from "./useVideoProviderConstraints";

/**
 * 某档在给定分辨率下的每秒积分(价目表规格档单价,已含分辨率系数)。该档不支持这个分辨率时
 * 退到它支持的第一个分辨率并把分辨率一起带回,让卡面写清是哪一档的价。
 */
export function tierPerSecond(
  book: PriceBook,
  cap: Pick<VideoProviderCapability, "model" | "supportedResolutions">,
  resolution: string
): { rate: number; resolution: string } | null {
  const res = cap.supportedResolutions.includes(resolution as VideoResolution)
    ? resolution
    : cap.supportedResolutions[0];
  if (!res) return null;
  const variant = book.variantByModel(SKU.video, cap.model, res);
  if (!variant) return null;
  const rate = book.unitExact(SKU.video, variant) ?? book.unit(SKU.video, variant);
  return rate == null ? null : { rate, resolution: res };
}

/** 「约 N 积分/秒」(不足 1 分的单价保留小数)。 */
export function perSecondText(rate: number): string {
  return `约 ${formatPerSecondRate(rate)} 积分/秒`;
}

/**
 * ★T5:后端档位描述(api VideoModelTiers.tagline)里写着「积分省」「积分最省」—— 演示机实测极速档
 *   并不比标准档便宜。价钱改由卡面每秒价说话,描述里的「省」字去掉(前端兜一层,api 改词后此处无副作用)。
 */
export function stripCheapClaim(tagline: string | null | undefined): string {
  return (tagline ?? "")
    .replace(/[、,，;；]?\s*积分(最)?省/g, "")
    .replace(/^[、,，;；\s]+/, "")
    .trim();
}

export interface AutoUpgradeNotice {
  /** 为什么升档(人话)。 */
  reason: string;
  fromLabel: string;
  toLabel: string;
  /** 用来问价的原默认档(provider/model)与它能出的最长秒数。 */
  from: { provider: string; model: string; maxSec: number };
}

/**
 * ★T4:智能选型在**自由创作直发**里因时长 / 分辨率装不下而升档(30s → 旗舰,报价 1425 → 7800)时,
 *   给一句原因。没有升档(或用户手动选型)→ null。
 */
export function autoUpgradeNotice(args: {
  caps: VideoCapabilitiesResponse;
  mode: GenerationMode;
  generateAudio: boolean;
  durationSec: number;
  resolution: string;
  effective: { provider: string; model: string };
}): AutoUpgradeNotice | null {
  const { caps, mode, generateAudio, durationSec, resolution, effective } = args;
  const base = autoPickModel(caps, mode, generateAudio);
  if (base.provider === effective.provider && base.model === effective.model) return null;
  const baseCap = caps.providers.find((p) => p.provider === base.provider && p.model === base.model);
  const effCap = caps.providers.find(
    (p) => p.provider === effective.provider && p.model === effective.model
  );
  if (!baseCap || !effCap) return null;
  const label = (c: VideoProviderCapability) =>
    videoProviderLabel(c.provider, c.model, c.tier ? c.label : null);
  const maxSec = baseCap.durationRange[1];
  const reasons: string[] = [];
  if (durationSec > maxSec) reasons.push(`「${label(baseCap)}」单段最长 ${maxSec} 秒,选了 ${durationSec} 秒`);
  if (!baseCap.supportedResolutions.includes(resolution as VideoResolution))
    reasons.push(`「${label(baseCap)}」不支持 ${resolution}`);
  if (reasons.length === 0) return null;
  return {
    reason: reasons.join(";"),
    fromLabel: label(baseCap),
    toLabel: label(effCap),
    from: { provider: base.provider, model: base.model, maxSec },
  };
}
